import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { Empty } from "Components/_Shared/Empty";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext } from "react";
import { InvestmentsNsureEvaluation } from "./InvestmentsNsureEvaluation";
import { InvestmentsNsureSelectionCard } from "./InvestmentsNsureSelectionCard";
import { NsureSelectionContext, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";

type Props = {
  readonly hideEvaluationWhenStacked?: boolean;
  /** Spacing units the panel drops by from `lg` up, to clear the left column's header row. */
  readonly topOffset?: number;
};

// alignSelf keeps the panel hugging its content — stretched next to the tall step-2 form it would read
// as a large empty surface. The drawer renders in a portal outside ScopedCssBaseline, so the 100% column
// needs border-box explicitly, otherwise any padding on it widens the tablet layout into a sideways
// scroll.
const panelWidth = { width: { xs: "100%", lg: 400 }, boxSizing: "border-box", flexShrink: 0, alignSelf: "flex-start" } as const;

export const InvestmentsNsureEvaluationPanel = ({ hideEvaluationWhenStacked, topOffset = 0 }: Props) => {
  const { t } = useResource();
  const { selection } = useContext(NsureSelectionContext);

  if (!selection) {
    return (
      <Box mt={{ xs: 0, lg: topOffset }} sx={panelWidth}>
        <Empty
          icon={<FactCheckOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />}
          text={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.SelectPortfolio)}
        />
      </Box>
    );
  }

  return (
    <Stack spacing={Spacing.M} mt={{ xs: 0, lg: topOffset }} sx={panelWidth}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px">
        {selection.type === NsureSelectionItemType.Portfolio
          ? t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.SelectionPanel.SelectedPortfolio)
          : t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.SelectionPanel.SelectedInstrument)}
      </Typography>
      <Stack
        spacing={Spacing.XXL}
        p={Spacing.XL}
        border="1px solid"
        borderColor="divider"
        borderRadius={Radius.L}
        bgcolor="grey.50"
        sx={{ boxShadow: 2 }}
      >
        <InvestmentsNsureSelectionCard selection={selection} />
        <Stack spacing={Spacing.XXL} sx={hideEvaluationWhenStacked ? { display: { xs: "none", lg: "flex" } } : undefined}>
          <Divider />
          <InvestmentsNsureEvaluation />
        </Stack>
      </Stack>
    </Stack>
  );
};
